import { type MappingTransform, TransformType } from '@today/types'
import { Button, Input } from '@today/ui-primitives'
import { TRANSFORM_OPTIONS } from './attribute-mapping-labels'

/**
 * Props for {@link TransformEditor}.
 *
 * @property label - What the transform applies to, for the controls' accessible names (e.g. "Given name").
 * @property value - The current transform.
 * @property onChange - Reports the new transform; switching type drops the previous type's parameter.
 * @property error - A refusal of this transform (`422 IDP_MAPPING_TRANSFORM_INVALID`), shown inline.
 * @property isDisabled - Disables every control.
 */
export interface TransformEditorProps {
	label: string
	value: MappingTransform
	onChange: (transform: MappingTransform) => void
	error?: string | null
	isDisabled?: boolean
}

const selectClass =
	'h-8 rounded-ds-md border border-ds-border bg-ds-surface px-2 text-ds-sm text-ds-text disabled:opacity-50'

/** A fresh transform of `type`, with the empty parameter that type owns. */
const transformOf = (type: TransformType): MappingTransform => {
	if (type === TransformType.SPLIT_FIRST) return { type, separator: '' }
	if (type === TransformType.REGEX_EXTRACT) return { type, pattern: '' }
	if (type === TransformType.LOOKUP) return { type, table: {} }
	return { type }
}

type Entry = [string, string]

type LookupRowProps = {
	entry: Entry
	n: number
	isDisabled: boolean
	onUpdate: (entry: Entry) => void
	onRemove: () => void
}

/** One lookup entry: key, value and remove. */
function LookupRow({ entry: [key, value], n, isDisabled, onUpdate, onRemove }: LookupRowProps) {
	return (
		<div className="flex items-center gap-1">
			<Input
				size="sm"
				aria-label={`Lookup key ${n}`}
				value={key}
				disabled={isDisabled}
				onChange={(event) => onUpdate([event.target.value, value])}
			/>
			<Input
				size="sm"
				aria-label={`Lookup value ${n}`}
				value={value}
				disabled={isDisabled}
				onChange={(event) => onUpdate([key, event.target.value])}
			/>
			<Button size="sm" variant="muted" isDisabled={isDisabled} onPress={onRemove}>
				Remove
			</Button>
		</div>
	)
}

type LookupEditorProps = {
	label: string
	table: Record<string, string>
	onChange: (table: Record<string, string>) => void
	isDisabled: boolean
}

/** The lookup table as editable rows. Keys may be blank while typing; the server refuses blanks. */
function LookupEditor({ label, table, onChange, isDisabled }: LookupEditorProps) {
	const entries = Object.entries(table)
	const commit = (next: Entry[]) => onChange(Object.fromEntries(next))
	return (
		<fieldset className="flex flex-col gap-1" aria-label={`Lookup table for ${label}`}>
			{entries.map((entry, index) => (
				<LookupRow
					// biome-ignore lint/suspicious/noArrayIndexKey: keys are editable (possibly blank); the position is the stable identity and every input is controlled.
					key={index}
					entry={entry}
					n={index + 1}
					isDisabled={isDisabled}
					onUpdate={(next) => commit(entries.map((old, i) => (i === index ? next : old)))}
					onRemove={() => commit(entries.filter((_, i) => i !== index))}
				/>
			))}
			<Button
				size="sm"
				variant="secondary"
				isDisabled={isDisabled || '' in table}
				onPress={() => commit([...entries, ['', '']])}
			>
				Add lookup entry
			</Button>
		</fieldset>
	)
}

/** The parameter input the transform type owns, if any. */
function TransformParameter({ label, value, onChange, isDisabled }: Required<Omit<TransformEditorProps, 'error'>>) {
	if (value.type === TransformType.SPLIT_FIRST) {
		return (
			<Input
				size="sm"
				aria-label={`Separator for ${label}`}
				value={value.separator ?? ''}
				disabled={isDisabled}
				onChange={(event) => onChange({ ...value, separator: event.target.value })}
			/>
		)
	}
	if (value.type === TransformType.REGEX_EXTRACT) {
		return (
			<Input
				size="sm"
				aria-label={`Pattern for ${label}`}
				value={value.pattern ?? ''}
				disabled={isDisabled}
				onChange={(event) => onChange({ ...value, pattern: event.target.value })}
			/>
		)
	}
	if (value.type === TransformType.LOOKUP) {
		return (
			<LookupEditor
				label={label}
				table={value.table ?? {}}
				isDisabled={isDisabled}
				onChange={(table) => onChange({ ...value, table })}
			/>
		)
	}
	return null
}

/**
 * A transform selector with the parameter input its type needs (REQ-203):
 * Split first → separator, Regex extract → pattern, Lookup → key/value table.
 * Pure and controlled; validation is the server's.
 */
export function TransformEditor({ label, value, onChange, error, isDisabled = false }: TransformEditorProps) {
	return (
		<div className="flex flex-col gap-1">
			<select
				aria-label={`Transform for ${label}`}
				className={selectClass}
				value={value.type}
				disabled={isDisabled}
				onChange={(event) => onChange(transformOf(event.target.value as TransformType))}
			>
				{TRANSFORM_OPTIONS.map((option) => (
					<option key={option.id} value={option.id}>
						{option.text}
					</option>
				))}
			</select>
			<TransformParameter label={label} value={value} onChange={onChange} isDisabled={isDisabled} />
			{error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
		</div>
	)
}
